/**
 * subplan-price
 *
 * 定期便の価格表示（通常価格の取り消し線 + 初回価格 + 2回目以降価格）を
 * バリアント切替に追従させるカスタム要素。
 *
 * 設計方針:
 * - 金額の文字列は Liquid の `money` フィルタで整形済みのものを JSON から引く。
 *   JS 側で通貨整形を再実装しない（表記揺れ・桁区切りの事故を防ぐため）。
 * - 定期購買アプリ（ハックルベリー社「定期購買」）が書き換える価格要素には一切触らない。
 *   このコンポーネントは `subplan-` 接頭辞の独自要素のみを更新する。
 * - テーマは `renderProductInfo()`（assets/global.js）でバリアント切替後に
 *   `productInfo:loaded` を dispatch する。それを購読して更新する。
 *   セクション HTML の差し替えに依存しないため、テーマ側の id 一致ロジックを変更する必要がない。
 */
class SubplanPrice extends HTMLElement {
  connectedCallback() {
    this.prices = this.readPrices();
    if (!this.prices) return;

    this.showPercent = this.dataset.showPercent !== 'false';

    this.onProductInfoLoaded = this.onProductInfoLoaded.bind(this);
    document.addEventListener('productInfo:loaded', this.onProductInfoLoaded);

    // バリアントセレクタが section HTML の差し替えを伴わない実装（select/radio の change）でも
    // 追従できるよう、フォームの id 入力の変化も直接拾う。
    this.onFormChange = this.onFormChange.bind(this);
    const form = this.productForm();
    if (form) form.addEventListener('change', this.onFormChange);
  }

  disconnectedCallback() {
    document.removeEventListener('productInfo:loaded', this.onProductInfoLoaded);
    const form = this.productForm();
    if (form) form.removeEventListener('change', this.onFormChange);
  }

  readPrices() {
    const script = this.querySelector('script[data-subplan-prices]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      // JSON が壊れている場合は Liquid が出した初期表示をそのまま残す（消さない）
      console.error('[subplan-price] 価格データの読み込みに失敗しました', e);
      return null;
    }
  }

  productForm() {
    const id = this.dataset.productForm;
    if (!id) return null;
    return document.getElementById(id);
  }

  currentVariantId() {
    const form = this.productForm();
    const input = form
      ? form.querySelector('[name="id"]')
      : document.querySelector('form[action*="/cart/add"] [name="id"]');
    return input ? String(input.value) : null;
  }

  onProductInfoLoaded() {
    this.update();
  }

  onFormChange(event) {
    if (event.target && event.target.name === 'id') this.update();
    else this.update();
  }

  update() {
    const variantId = this.currentVariantId();
    if (!variantId) return;

    const data = this.prices[variantId];

    // 定期プランを持たないバリアントに切り替わった場合は表示を隠す。
    // （プランなしのバリアントに定期価格を出したままにしないため）
    if (!data) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    this.setText('[data-subplan-regular]', data.regular);
    this.setText('[data-subplan-first]', data.first);
    this.setText('[data-subplan-recurring]', data.recurring);

    if (this.showPercent) {
      this.setText('[data-subplan-first-percent]', `${data.firstPercent}% OFF`);
      this.setText('[data-subplan-recurring-percent]', `${data.recurringPercent}% OFF`);
    }
  }

  setText(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value;
  }
}

if (!customElements.get('subplan-price')) {
  customElements.define('subplan-price', SubplanPrice);
}
